import { useRef } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";

import { MaximizeIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useLiveMonitoringLiveMonitoringGetQuery } from "api";
import AppVersion from "components/AppVersion/AppVersion";
import { DynamicButton } from "components/DynamicButton/DynamicButton";
import { SUPPORTED_DEVICE_TYPES } from "constants/deviceConstants";
import { useFormat } from "hooks/useFormat";
import { useGetZones } from "hooks/useGetZones";
import { useIsFullscreen } from "hooks/useIsFullscreen";

import { ScaleSelector } from "./components/ScaleSelector";
import { SubjectOverview } from "./components/SubjectOverview";
import { groupData } from "./utils/groupLiveData";

import DateTimeText from "../components/DateTimeText";
import { ZoneSelector } from "../components/ZoneSelector";
import { useMonitoringStore } from "../monitoringStore";

export function DashboardScreen() {
  const { t } = useTranslation();
  const { format } = useFormat();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isFullscreen = useIsFullscreen();

  const { zones, isLoading: isLoadingZones } = useGetZones();

  const selectedZoneIds = useMonitoringStore((state) => state.selectedZoneIds);

  const selectedZones =
    selectedZoneIds
      ?.map((zoneId) => zones.find((zone) => zoneId === zone.id))
      .filter((zone) => zone !== undefined) ?? zones;

  const {
    data: liveMonitor,
    isLoading: isLoadingData,
    fulfilledTimeStamp,
  } = useLiveMonitoringLiveMonitoringGetQuery(
    {
      zones: selectedZones.map(({ id }) => id),
      deviceTypes: SUPPORTED_DEVICE_TYPES,
      includeUnassignedDevices: true,
    },
    {
      skip: selectedZones.length === 0,
      pollingInterval: 10_000,
    },
  );

  const liveData = groupData(zones, selectedZones, liveMonitor?.data ?? []);

  if (isLoadingZones || isLoadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      ref={componentRef}
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        backgroundColor: "background.default",
      }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        sx={{
          gap: 2,
          paddingInline: { xs: 1.5, md: 2.5, lg: 3 },
          paddingBlock: 1,
        }}
      >
        <DateTimeText
          sx={{ display: { xs: "none", md: "initial" }, flexGrow: 1 }}
        />
        <Typography
          color={"textSecondary"}
          variant={"bodySmallRegular"}
          sx={{ flexGrow: { xs: 1, md: 0 } }}
        >
          {fulfilledTimeStamp
            ? `${t("Data updated")}: ${format(fulfilledTimeStamp, "pp")}`
            : ""}
        </Typography>
        {isFullscreen && <ScaleSelector />}
        <DynamicButton
          startIcon={<MaximizeIcon />}
          color={"info"}
          onClick={async () => {
            if (isFullscreen) {
              await document.exitFullscreen();
            } else {
              await componentRef.current?.requestFullscreen?.();
            }
          }}
        >
          {isFullscreen ? t("exit_fullscreen") : t("enter_fullscreen")}
        </DynamicButton>
        {!isFullscreen && <ZoneSelector zones={zones} />}
      </Stack>
      <Box
        sx={{
          paddingInline: { xs: 1.5, md: 2.5, lg: 3 },
          flexGrow: 1,
          paddingBottom: 2,
        }}
      >
        <SubjectOverview liveData={liveData} />
      </Box>
      <AppVersion />
    </Box>
  );
}
